// frontend/src/components/settings/ExportColumnsDialog.jsx
//
// Which columns this store's spreadsheet exports carry — one screen for all of
// them, opened from the Exports tile in Store Settings.
//
// Two panes: the export a merchant came here to change is one click away, and
// the column list has the whole dialog width.
//
// It owns its own load and save (GET/PATCH /api/export-columns/): the payload
// is a server-derived catalog the settings page has no other use for.
//
// The dialog does NOT re-implement the server's rules. Which boxes are
// disabled comes from the catalog's `hideable` flag — a server fact, not a
// copy of the rule that produced it — and the bounds that are about a SET of
// columns rather than one ("at least one column must stay", "keep one of the
// identifiers the importer matches rows by") are left to the PATCH, whose
// per-entity error is shown inline on the export it names — see the
// mutation's onError.

import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { apiJson, translateRequestError } from '../../queries/api';
import { DATA } from '../../queries/dataChanged';
import { useApiMutation } from '../../queries/useApiMutation';
import * as keys from '../../queries/keys';
import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../components/common/appToastBus';

const ENDPOINT = '/api/export-columns/';

/** Merchant-facing name of one export column. */
const columnLabel = (t, column) => (column.i18n_key ? t(column.i18n_key) : column.label);

export default function ExportColumnsDialog({ open, onClose }) {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const queryClient = useQueryClient();
  const storeId = selectedStore?.id;

  const { data, isLoading, error } = useQuery({
    queryKey: keys.exportColumns.all(storeId),
    queryFn: () => apiJson.get(ENDPOINT),
    enabled: open && !!storeId,
  });

  // `null` until the merchant touches something, so a background refetch of
  // the server state is never mistaken for an edit and the Save button stays
  // honest about whether there is anything to save.
  const [edits, setEdits] = useState(null);
  const [activeEdit, setActiveEdit] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const entities = useMemo(() => data?.entities || [], [data]);
  const hidden = edits ?? data?.hidden ?? {};
  // Defaults to the first export. DERIVED rather than seeded in an effect: an
  // effect that seeds state renders once with the wrong value first, and the
  // pass in between is the one that shows an empty column list.
  const activeKey = activeEdit ?? entities[0]?.key ?? null;

  // Closing discards unsaved edits, so reopening starts from the server's
  // answer rather than a half-finished one from last time. In the handler, not
  // an effect on `open` — the reset belongs to the act of closing.
  const handleClose = useCallback(() => {
    setEdits(null);
    setActiveEdit(null);
    setFieldErrors({});
    onClose();
  }, [onClose]);

  const active = entities.find((entity) => entity.key === activeKey) || null;
  const activeHidden = hidden[activeKey] || [];

  const toggle = useCallback((entityKey, field) => {
    setFieldErrors({});
    setEdits((current) => {
      const base = current ?? data?.hidden ?? {};
      const was = base[entityKey] || [];
      const next = was.includes(field)
        ? was.filter((f) => f !== field)
        : [...was, field];
      return { ...base, [entityKey]: next };
    });
  }, [data]);

  const showAll = useCallback((entityKey) => {
    setFieldErrors({});
    setEdits((current) => ({ ...(current ?? data?.hidden ?? {}), [entityKey]: [] }));
  }, [data]);

  // The server's answer goes straight into this dialog's own key, so the
  // dialog shows what it saved before the settings readers read again.
  const mutation = useApiMutation({
    mutationFn: (selection) => apiJson.patch(ENDPOINT, { hidden: selection }),
    changes: [DATA.settings],
    onSuccess: (payload) => {
      queryClient.setQueryData(keys.exportColumns.all(storeId), payload);
      setEdits(null);
      setFieldErrors({});
      showToast(t('storeSettings.exportColumns.saved'), 'success');
      handleClose();
    },
    onError: (err) => {
      // Per-column detail from the server, attached to the rows it names.
      // `.body` — ApiError's parsed payload; DomainError merges `extra` into
      // the top level, so `errors` sits directly on it.
      const errors = err?.body?.errors || {};
      setFieldErrors(errors);
      // A refusal is about ONE export, and the dialog edits all of them before
      // saving once — so the pane on screen at Save is often not the pane the
      // server refused. Switch to it: `export_columns.<entity>` is an
      // entity-level message (three segments would be one column of it), and
      // rendering it behind another tab is the same as not rendering it.
      const refused = Object.keys(errors)
        .map((path) => path.split('.'))
        .find((parts) => parts.length === 2);
      if (refused && refused[1] !== activeKey) setActiveEdit(refused[1]);
      showToast(
        translateRequestError(err, t, 'storeSettings.exportColumns.saveFailed'),
        'error',
      );
    },
  });

  const entityError = fieldErrors[`export_columns.${activeKey}`];

  return (
    <Dialog open={open} onClose={handleClose} className="dialog-form">
      <DialogTitle>{t('storeSettings.exportColumns.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {t('storeSettings.exportColumns.hint')}
        </Typography>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && <Alert severity="error">{t('storeSettings.exportColumns.loadFailed')}</Alert>}

        {!isLoading && !error && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <List dense sx={{ width: 220, flexShrink: 0, borderRight: 1, borderColor: 'divider' }}>
              {entities.map((entity) => {
                const count = (hidden[entity.key] || []).length;
                return (
                  <ListItemButton
                    key={entity.key}
                    selected={entity.key === activeKey}
                    onClick={() => setActiveEdit(entity.key)}
                  >
                    <ListItemText
                      primary={t(`storeSettings.exportColumns.entity.${entity.key}`)}
                      secondary={count
                        ? t('storeSettings.exportColumns.hiddenCount', { count })
                        : t('storeSettings.exportColumns.allColumns')}
                    />
                  </ListItemButton>
                );
              })}
            </List>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              {entityError && <Alert severity="error" sx={{ mb: 1 }}>{entityError}</Alert>}
              {active && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Button
                      size="small"
                      onClick={() => showAll(active.key)}
                      disabled={!activeHidden.length}
                    >
                      {t('storeSettings.exportColumns.showAll')}
                    </Button>
                  </Box>
                  <FormGroup>
                    {active.columns.map((column) => (
                      <FormControlLabel
                        key={column.field}
                        control={
                          <Checkbox
                            size="small"
                            checked={!activeHidden.includes(column.field)}
                            disabled={!column.hideable || mutation.isPending}
                            onChange={() => toggle(active.key, column.field)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {columnLabel(t, column)}
                            {!column.hideable && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {t('storeSettings.exportColumns.alwaysIncluded')}
                              </Typography>
                            )}
                          </Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                </>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate(hidden)}
          disabled={edits === null}
          loading={mutation.isPending}
        >
          {t('btn.saveChanges')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
